import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { templateService } from '../services/templateService'
import { patientService } from '../services/patientService'
import { consultationService } from '../services/consultationService'
import { patientFileService } from '../services/patientFileService'
import { useAuth } from '../contexts/AuthContext'
import { formatPatientName } from '../utils/name'
import './GenerateDocumentModal.css'

interface GenerateDocumentModalProps {
  templateId: string
  patientId: string
  consultationId?: string
  onClose: () => void
  onSuccess?: () => void
}

export function GenerateDocumentModal({
  templateId,
  patientId,
  consultationId,
  onClose,
  onSuccess,
}: GenerateDocumentModalProps) {
  const [generating, setGenerating] = useState(false)
  const [markAsInstructions, setMarkAsInstructions] = useState(false)
  const queryClient = useQueryClient()
  const { user } = useAuth()

  const createSafeFileName = (baseName: string) => {
    return baseName
      .replace(/[^a-zA-Z0-9._-]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '')
      .substring(0, 150)
  }

  const base64ToFile = (base64: string, fileName: string, mimeType?: string) => {
    const byteCharacters = atob(base64.trim())
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    const blob = new Blob([byteArray], { type: mimeType || 'application/vnd.oasis.opendocument.text' })
    return new File([blob], fileName, { type: mimeType || 'application/vnd.oasis.opendocument.text' })
  }

  const { data: template } = useQuery({
    queryKey: ['template', templateId],
    queryFn: () => templateService.getById(templateId),
    enabled: !!templateId,
  })

  const { data: patient } = useQuery({
    queryKey: ['patient', patientId],
    queryFn: () => patientService.getById(patientId),
    enabled: !!patientId,
  })

  const generateMutation = useMutation({
    mutationFn: async () => {
      return templateService.generateDocument({
        templateId,
        patientId,
        consultationId,
      })
    },
    onSuccess: async (response) => {
      try {
        const templateCategory = template?.category || 'document'
        const patientName = patient ? formatPatientName(patient as any) : 'patient'
        const dateStamp = new Date().toISOString().split('T')[0]
        const safeBaseName = createSafeFileName(`${patientName}_${dateStamp}_${templateCategory}`)
        const fileName = safeBaseName.endsWith('.odt') ? safeBaseName : `${safeBaseName}.odt`
        const file = base64ToFile(response.content, fileName, response.mimeType)

        await patientFileService.upload(patientId, {
          file,
          displayName: fileName,
          description: template?.name
            ? `Generated from template: ${template.name}`
            : 'Generated document',
          category: 'medical-record',
        })

        queryClient.invalidateQueries({ queryKey: ['patient-files', patientId] })
        queryClient.invalidateQueries({ queryKey: ['patient-files', 'patient', patientId] })

        try {
          templateService.downloadDocument(response.content, response.fileName, response.mimeType)
        } catch (downloadError) {
          console.error('[GenerateDocumentModal] Download failed:', downloadError)
        }
        
        // Create or update consultation artefact
        if (template) {
          try {
            // Map template category to artefact type
            const artefactTypeMap: Record<string, 'referral-letter' | 'prescription' | 'certificate' | 'lab-order' | 'imaging-order' | 'other'> = {
              'letter': 'referral-letter',
              'referral': 'referral-letter',
              'prescription': 'prescription',
              'certificate': 'certificate',
              'form': 'other',
              'report': 'other',
              'instruction': 'other',
              'other': 'other',
            }
            
            const artefactType = artefactTypeMap[template.category] || 'other'
            
            let targetConsultationId = consultationId
            
            // If no consultationId, create a minimal "document-only" consultation
            if (!targetConsultationId) {
              try {
                // Use current user's ID as staffId, or a default if not available
                const staffId = user?.id || 'system'
                
                const documentConsultation = await consultationService.create({
                  patientId,
                  staffId,
                  status: 'completed',
                  consultationDate: new Date().toISOString().split('T')[0],
                  reason: `Document generation: ${template.name}`,
                  notes: `Document generated from template outside of consultation context.`,
                })
                targetConsultationId = documentConsultation.id
                console.log('[GenerateDocumentModal] Created document-only consultation:', targetConsultationId)
              } catch (error) {
                console.error('[GenerateDocumentModal] Failed to create document-only consultation:', error)
                // Continue anyway - we'll try to add artefact if consultation was created
              }
            }
            
            // Add artefact to consultation (either existing or newly created)
            if (targetConsultationId) {
              const artefact = await consultationService.addArtefact(targetConsultationId, {
                type: artefactType,
                title: template.name || response.fileName,
                description: template.description || `Generated from template: ${template.name}`,
                status: 'issued',
                content: response.content,
                referenceNumber: response.documentId,
                containsPatientInstructions: markAsInstructions,
              })
              
              // If marked as instructions document, update consultation instructions
              if (markAsInstructions && artefact.id) {
                try {
                  const consultation = await consultationService.getById(targetConsultationId)
                  const currentDocumentIds = consultation.instructionDocumentIds || []
                  if (!currentDocumentIds.includes(artefact.id)) {
                    await consultationService.update(targetConsultationId, {
                      instructionDocumentIds: [...currentDocumentIds, artefact.id],
                    })
                  }
                } catch (error) {
                  console.error('[GenerateDocumentModal] Failed to link document to instructions:', error)
                  // Don't fail the whole operation if linking fails
                }
              }
              
              // Invalidate consultation queries to refresh the list
              queryClient.invalidateQueries({ queryKey: ['consultation', targetConsultationId] })
              queryClient.invalidateQueries({ queryKey: ['consultations', 'patient', patientId] })
              queryClient.invalidateQueries({ queryKey: ['consultations'] })
            }
          } catch (error) {
            console.error('[GenerateDocumentModal] Failed to create artefact:', error)
            // Don't fail the whole operation if artefact creation fails
          }
        }

        onSuccess?.()
        onClose()
      } catch (error: any) {
        console.error('[GenerateDocumentModal] Failed to save patient file:', error)
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save patient file'
        alert(`Error: ${errorMessage}`)
      } finally {
        setGenerating(false)
      }
    },
    onError: (error: any) => {
      console.error('[GenerateDocumentModal] Mutation error:', error)
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to generate document'
      console.error('[GenerateDocumentModal] Error message:', errorMessage)
      alert(`Error: ${errorMessage}`)
      setGenerating(false)
    },
  })

  const handleGenerate = async () => {
    console.log('[GenerateDocumentModal] Starting document generation:', { templateId, patientId, consultationId })
    setGenerating(true)
    try {
      await generateMutation.mutateAsync()
    } catch (error: any) {
      console.error('[GenerateDocumentModal] Generation error:', error)
      // Error is already handled in onError callback
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Generate Document</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {template && (
            <div className="template-info">
              <h3>{template.name}</h3>
              {template.description && <p>{template.description}</p>}
            </div>
          )}

          {patient && (
            <div className="patient-info">
              <h3>Patient</h3>
              <p>
                {formatPatientName(patient as any)}
                {patient.patientNumber && ` (${patient.patientNumber})`}
              </p>
            </div>
          )}

          {template?.variables && template.variables.length > 0 && (
            <div className="variables-preview">
              <h3>Template Variables</h3>
              <p className="variables-hint">
                The following variables will be populated from patient data:
              </p>
              <ul className="variables-list">
                {template.variables.slice(0, 10).map((variable, index) => (
                  <li key={index}>
                    <code>{variable.name}</code> - {variable.label}
                  </li>
                ))}
                {template.variables.length > 10 && (
                  <li className="variables-more">
                    ... and {template.variables.length - 10} more
                  </li>
                )}
              </ul>
            </div>
          )}

          {consultationId && (
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={markAsInstructions}
                  onChange={(e) => setMarkAsInstructions(e.target.checked)}
                />
                <span>Mark as Patient Instructions Document</span>
              </label>
              <p className="form-hint">
                When checked, this document will be linked to the consultation's patient instructions.
              </p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={generating}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleGenerate}
            disabled={generating || !template || !patient}
          >
            {generating ? 'Generating...' : 'Generate Document'}
          </button>
        </div>
      </div>
    </div>
  )
}














